import { Typography, Table, Col } from "antd";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { documentsColumn } from "utils/tableColums";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";

const { Title } = Typography;

const MEETING_LINK_KEYS = ["docs", "attendance", "recommendation"];

const LINK_LABELS = {
  docs: "View Minutes",
  attendance: "View Attendance",
  recommendation: "View Document",
};

const AuditCommiteeMeeting = forwardRef(({
  meetings,
  meetingColumns,
  district,
  year,
  hideComment,
  savedAssessmentData,
  isAssessmentStarted,
}, ref) => {
  const displayMeetings = useMemo(() => {
    if (!isAssessmentStarted || !savedAssessmentData) return meetings;
    return savedAssessmentData.meetings ?? savedAssessmentData;
  }, [isAssessmentStarted, savedAssessmentData, meetings]);

  const meetingRows = displayMeetings?.data ?? [];
  const documentLinks = displayMeetings?.links ?? meetings?.links ?? [];

  const meetingTableColumns = useMemo(
    () => (meetingColumns ?? []).map((col) =>
      MEETING_LINK_KEYS.includes(col.dataIndex)
        ? {
            ...col,
            render: (value) => renderDpatEvidenceLink(
              value,
              LINK_LABELS[col.dataIndex] ?? "View Document",
            ),
          }
        : col
    ),
    [meetingColumns],
  );

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
            ...col,
            render: (value) => renderDpatEvidenceLink(value, "View Document"),
          }
        : col
    ),
    [],
  );

  const DPAT_SLUG = "c3.0-3.3";

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI3",
      area: "Public Financial Management and Auditing",
      meetings: {
        ...meetings,
        ...displayMeetings,
        links: displayMeetings?.links ?? meetings?.links,
      },
    }),
  }));

  return (
    <Comment
      data={displayMeetings}
      year={year}
      districtId={district}
      tableCommentedId={`c3.0-3.3-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />

          <Col align="start">
            <CiResultDisplay slug={DPAT_SLUG} fulfillment={displayMeetings?.fulfillment} />
          </Col>

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Audit Committee Meetings</Title>
          {meetingRows.length > 0 && (
            <Table
              columns={meetingTableColumns}
              dataSource={meetingRows}
              pagination={false}
              bordered
              rowKey={(record, index) => record.key || index}
            />
          )}

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Attached Documents</Title>
          <Table
            columns={documentTableColumns}
            dataSource={documentLinks}
            pagination={false}
            bordered
            rowKey="title"
          />

          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default AuditCommiteeMeeting;
